/**
 * AuthScreen.tsx — Login/Register UI (task B10).
 *
 * Rules honored:
 *  - Client-side validation mirrors the shared contracts (same rules and
 *    messages as the server) for instant feedback; the server remains
 *    authoritative.
 *  - Loading/disabled state prevents duplicate submits.
 *  - API errors are shown verbatim (they are safe, non-secret strings).
 *  - Passwords are held in React state only — never persisted to
 *    localStorage/sessionStorage. The CSRF token lives in the in-memory
 *    holder in ../api.ts (also never persisted).
 */
import { useRef, useState, type FormEvent } from 'react';
import {
  ApiClientError,
  passwordSchema,
  usernameSchema,
  type UserDto,
} from '@shared';
import { apiV1 } from '../api';

type Mode = 'login' | 'register';

interface AuthScreenProps {
  onAuthenticated: (user: UserDto) => void;
}

function fieldError(schema: typeof usernameSchema | typeof passwordSchema, value: string): string | null {
  const parsed = schema.safeParse(value);
  if (parsed.success) return null;
  const issue = parsed.error.issues[0];
  return issue ? issue.message : 'Invalid value.';
}

export function AuthScreen({ onAuthenticated }: AuthScreenProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // K02: synchronous duplicate-submit guard. The `submitting` STATE can't
  // stop a second click in the same tick (stale closure); the ref can.
  const submittingRef = useRef(false);

  function switchMode(next: Mode): void {
    setMode(next);
    setError(null);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);

    const usernameProblem = fieldError(usernameSchema, username);
    if (usernameProblem) {
      setError(usernameProblem);
      return;
    }
    const passwordProblem = fieldError(passwordSchema, password);
    if (passwordProblem) {
      setError(passwordProblem);
      return;
    }
    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'register') {
        await apiV1.register({ username, password });
        // Registration does not log in (locked API) — log in immediately
        // afterwards so the user lands in the app.
        const data = await apiV1.login({ username, password });
        onAuthenticated(data.user);
      } else {
        const data = await apiV1.login({ username, password });
        onAuthenticated(data.user);
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <h1>Secret Santa</h1>
      <div className="auth-tabs" role="tablist" aria-label="Authentication">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'login'}
          className={mode === 'login' ? 'active' : ''}
          onClick={() => switchMode('login')}
          disabled={submitting}
        >
          Log in
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'register'}
          className={mode === 'register' ? 'active' : ''}
          onClick={() => switchMode('register')}
          disabled={submitting}
        >
          Create account
        </button>
      </div>

      <form className="auth-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <label>
          Username
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            maxLength={32}
            disabled={submitting}
            aria-describedby={error ? 'auth-error' : undefined}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            maxLength={128}
            disabled={submitting}
          />
        </label>
        {mode === 'register' && (
          <label>
            Confirm password
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              maxLength={128}
              disabled={submitting}
            />
          </label>
        )}

        {error && (
          <p id="auth-error" className="auth-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="auth-submit" disabled={submitting}>
          {submitting ? (mode === 'login' ? 'Logging in…' : 'Creating account…') : mode === 'login' ? 'Log in' : 'Create account'}
        </button>
      </form>
    </div>
  );
}
